import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info, X } from 'lucide-react';

interface LegalNoticeBannerProps {
  storageKey: string;
  messageKey: string;
  learnMoreLink?: string;
  className?: string;
}

export default function LegalNoticeBanner({ storageKey, messageKey, learnMoreLink, className }: LegalNoticeBannerProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === 'true',
  );

  function handleDismiss() {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  }

  function handleReshow() {
    localStorage.removeItem(storageKey);
    setDismissed(false);
  }

  if (dismissed) {
    return (
      <div className={`mb-5 flex justify-end ${className ?? ''}`}>
        <button
          onClick={handleReshow}
          className="flex items-center gap-1 rounded-full border border-blue-300/30 bg-blue-500/5 px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-500/10 transition-colors dark:text-blue-300 dark:border-blue-400/30"
          aria-label={t('legal.showNotice')}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="status"
      className={`mb-5 rounded-lg border border-blue-300/30 bg-blue-500/5 px-4 py-3 text-sm text-blue-700 dark:text-blue-300 dark:border-blue-400/30 ${className ?? ''}`}
    >
      <div className="flex items-start gap-2.5">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="flex-1">
          {t(messageKey)}
          {learnMoreLink && (
            <>
              {' '}
              <Link to={learnMoreLink} className="underline hover:text-blue-900 dark:hover:text-blue-100">
                {t('legal.learnMore')}
              </Link>
            </>
          )}
        </p>
        <button
          onClick={handleDismiss}
          className="shrink-0 rounded-full p-0.5 hover:bg-blue-500/10 transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
